// Горната лента на работен лист (Доставка, Изписване, Инвентаризация,
// Прехвърляне): заглавието И реквизитите на документа в ЕДНА лента, залепена
// за горния край — стегната като старата „лента с реквизити" и ясна като хедър.
//
//   ‹ Доставка                                         ● Само вие редактирате
//   ФАКТУРА Нова доставка │ ДОСТАВЧИК  ТИП  ФАКТУРА №  СКЛАД  …  БЕЛЕЖКА
//
// Първият ред е тънък (назад + присъствие), вторият — заглавие и полета на един
// ред; при тесен екран полетата се пренасят, а на телефон са едно под друго.
import type { ReactNode } from "react";
import { LeftOutlined, MenuOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useInventoryShell } from "../shell/InventoryShellContext";

type Props = {
  back: { label: string; onClick: () => void };
  title: ReactNode;
  // хапче до заглавието (тип документ, вид)
  titleAddon?: ReactNode;
  // горе вдясно (присъствие, статус)
  right?: ReactNode;
  // полетата — `WorksheetField`
  children: ReactNode;
};

export const WorksheetTopBand = ({ back, title, titleAddon, right, children }: Props) => {
  const { t } = useTranslation();
  const { isMenuButtonVisible, openMenu, isNarrow } = useInventoryShell();
  return (
    <header
      className={`shrink-0 border-b border-shade-2 bg-panel ${isNarrow ? "px-3.5 pb-3 pt-2" : "px-6 pb-3.5 pt-2.5"}`}
    >
      <div className="flex min-h-[26px] items-center gap-3">
        {isMenuButtonVisible && (
          <button
            type="button"
            onClick={openMenu}
            aria-label={t("inventoryPage.openMenu")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-lg border border-shade-3 bg-surface text-ink"
          >
            <MenuOutlined className="text-sm" />
          </button>
        )}
        <button
          type="button"
          onClick={back.onClick}
          className="flex cursor-pointer items-center gap-1.5 border-none bg-transparent p-0 text-[12.5px] leading-[normal] text-body hover:text-ink"
        >
          <LeftOutlined className="text-[10px]" />
          {back.label}
        </button>
        {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
      </div>
      {/* Заглавието стои на височината на ПЪРВИЯ ред полета (под етикета им) —
          при пренасяне на полетата не пада до последния ред */}
      <div className={`mt-1.5 flex flex-wrap items-start ${isNarrow ? "gap-3" : "gap-x-5 gap-y-3"}`}>
        <div className={`flex min-w-0 items-center gap-2.5 ${isNarrow ? "w-full" : "mt-[15px] h-10"}`}>
          {titleAddon}
          <h1
            className={`m-0 truncate font-bold leading-[1.2] tracking-[-0.01em] text-ink ${
              isNarrow ? "text-lg" : "text-xl"
            }`}
          >
            {title}
          </h1>
        </div>
        {!isNarrow && <div aria-hidden className="mt-[11px] h-12 w-px bg-shade-2" />}
        <div className={`flex min-w-0 flex-1 flex-wrap items-end ${isNarrow ? "gap-2.5" : "gap-x-3 gap-y-2.5"}`}>
          {children}
        </div>
      </div>
    </header>
  );
};

// Ширините са фиксирани (както в старата лента) — окото намира полето на
// същото място във всеки документ; `grow` поема остатъка (бележката).
const FIELD_WIDTH = {
  sm: "w-[150px]",
  md: "w-[185px]",
  lg: "w-[250px]",
  auto: "w-auto",
  grow: "min-w-[180px] flex-1",
} as const;

export const WorksheetField = ({
  label,
  required,
  size = "md",
  children,
}: {
  label: string;
  required?: boolean;
  size?: keyof typeof FIELD_WIDTH;
  children: ReactNode;
}) => {
  const { isNarrow } = useInventoryShell();
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${isNarrow ? "w-full" : FIELD_WIDTH[size]}`}>
      <span className="text-[10px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
        {label}
        {required && <span className="text-danger-ink"> *</span>}
      </span>
      {children}
    </div>
  );
};

export default WorksheetTopBand;
